import React from 'react'
import PropTypes from 'prop-types'
import { isInvestor } from '../../../../global/authentication/authentication-manager.js'
import OverviewContent from './OverviewContent.jsx'
import OverviewInvestmentObjective from './OverviewInvestmentObjective.jsx'
import OverviewNewNoteworthy from './OverviewNewNoteworthy.jsx'
import OverviewPortfolioManagers from './OverviewPortfolioManagers.jsx'
import OverviewPerformance from './OverviewPerformance.jsx'
import OverviewRisk from './OverviewRisk.jsx'
import OverviewYields from './OverviewYields.jsx'
import OverviewFees from './OverviewFees.jsx'
import OverviewFundAtAGlance from './OverviewFundAtAGlance.jsx'
import OverviewIndexAtAGlance from './OverviewIndexAtAGlance.jsx'
import {
  isDisplayNoMessageData,
  isPresented,
} from '../../Helpers/FundUtility.js'
import DataNotAvailable from '../../Helpers/DataNotAvailable.js'
import './Overview.css'
import OverviewPortfolioStrategy from './OverviewPortfolioStrategy.jsx'
import OverviewUnderlyingFunds from './OverviewUnderlyingFunds.jsx'
import {
  FUTURE_SCHOLAR_529,
  CLOSED_END_FUND,
  MODEL_PORTFOLIOS,
} from '../../Helpers/FundTypes.js'
import ErrorBoundary from '../Performance/ErrorBoundary.jsx'

function Overview({ fund }) {
  const renderSevenYields =
    fund.layoutID === '8' &&
    fund.sevenDayYields &&
    isPresented(fund, 'sevenDayYields')
  const sevenDayYieldsData = isPresented(fund, 'sevenDayYields')
    ? fund.sevenDayYields
    : null
  const renderRiskLayout24 = fund.layoutID === '24'
  const renderRiskNoDataMessage = isDisplayNoMessageData(fund, 'riskDetail')

  const isRenderRisk =
    (isPresented(fund, 'feesAndExpenseDetail') ||
      isDisplayNoMessageData(fund, 'feesAndExpenseDetail') ||
      isPresented(fund, 'riskDetail') ||
      isDisplayNoMessageData(fund, 'riskDetail') ||
      isPresented(fund, 'performanceDetail') ||
      isDisplayNoMessageData(fund, 'performanceDetail')) &&
    fund.fundGroup !== FUTURE_SCHOLAR_529

  return (
    <section
      className="u-adel-reset"
      data-section-content="fund-story"
      id="fund-story"
    >
      <div className="u-sizeConstrained">
        <div id="js-fund-message"></div>
        <div className="section-content u-paddingTop6gu">
          <div className="u-inlineBlock u-marginBottom2gu">
            <h2>Overview</h2>
          </div>

          {fund.fundStory && <OverviewContent fundStory={fund.fundStory} />}

          {fund.portfolioStrategy && (
            <OverviewPortfolioStrategy
              portfolioStrategy={fund.portfolioStrategy}
            />
          )}

          {fund.underlyingFunds &&
            fund.underlyingFunds.underLyingFundsDetails && (
              <OverviewUnderlyingFunds underlyingFunds={fund.underlyingFunds} />
            )}

          {/* null-check: legacy ucm requires that the 'investment objective' not be empty, so '&nbsp;'  */}
          {/* is used as a place holder - therefore check is against '&nbsp;'  */}
          {isInvestor() &&
            fund &&
            fund.investmentObjective &&
            fund.investmentObjective !== '&nbsp;' && (
              <OverviewInvestmentObjective
                investorFundDetail={fund.investmentObjective}
              />
            )}
        </div>
        {fund.portfolioManagers &&
          fund.portfolioManagers.length > 0 &&
          isPresented(fund, 'portfolioManagement') && (
            <OverviewPortfolioManagers
              portfolioManagers={fund.portfolioManagers}
            />
          )}
        {fund.newNoteWorthy && (
          <OverviewNewNoteworthy newNoteWorthy={fund.newNoteWorthy} />
        )}
        {isRenderRisk && (
          <>
            <div
              className="Grid Grid--fill u-paddingVert8gu u-sm-flexCol"
              id="performance-risk-fees-content"
            >
              {fund.performanceDetail &&
                isPresented(fund, 'performanceDetail') && (
                  <OverviewPerformance
                    fundName={fund.fundName}
                    performance={fund.performanceDetail}
                  />
                )}

              {isDisplayNoMessageData(fund, 'performanceDetail') && (
                <div className="u-paddingRight4gu u-sm-paddingRight0 u-sm-paddingBottom3gu">
                  <DataNotAvailable title={'Performance'} />
                </div>
              )}

              {(renderSevenYields ||
                renderRiskLayout24 ||
                isPresented(fund, 'riskDetail') ||
                renderRiskNoDataMessage) && (
                <div
                  className="u-borderColorNeutralLight u-borderBottom0 u-borderTop0 u-borderLeft u-borderRight0 u-paddingHoriz4gu u-sm-border0 u-sm-paddingHoriz0 u-sm-paddingVert6gu"
                  id="risk-yields-content"
                >
                  {(fund.riskDetail ||
                    renderRiskLayout24 ||
                    renderSevenYields) && (
                    <>
                      {(fund.riskDetail || renderRiskLayout24) && (
                        <OverviewRisk
                          fund={fund}
                          renderRiskLayout24={renderRiskLayout24}
                        />
                      )}
                      {renderSevenYields && (
                        <ErrorBoundary
                          message="Yields information is currently unavailable due to a technical issue. Please try again later."
                          title="Yields (%)"
                        >
                          <OverviewYields
                            sevenDayYields={fund.sevenDayYields}
                          />
                        </ErrorBoundary>
                      )}
                    </>
                  )}
                  {renderRiskNoDataMessage && (
                    <DataNotAvailable title={'Risk'} />
                  )}
                </div>
              )}

              {fund.feesAndExpenseDetail &&
              !(
                fund.feesAndExpenseDetail.grossExpense === '-' &&
                fund.feesAndExpenseDetail.expenseNet === '-' &&
                fund.feesAndExpenseDetail.maxSalesCharge === '-'
              ) &&
              isPresented(fund, 'feesAndExpenseDetail') ? (
                <OverviewFees
                  feesAndExpenses={fund.feesAndExpenseDetail}
                  fundGroup={fund.fundGroup}
                />
              ) : (
                <div className="u-borderColorNeutralLight u-borderBottom0 u-borderTop0 u-borderLeft u-borderRight0 u-paddingHoriz4gu u-sm-border0 u-sm-paddingHoriz0 u-sm-paddingVert6gu">
                  <DataNotAvailable title={'Fees'} />
                </div>
              )}
            </div>
          </>
        )}

        {fund.summary &&
          (fund.summary.fundBasicDetail || fund.summary.modelBasicDetail) &&
          (isPresented(fund, 'fundsAtAGlance') ||
            fund.fundGroup === FUTURE_SCHOLAR_529 ||
            fund.fundGroup === CLOSED_END_FUND ||
            fund.fundGroup === MODEL_PORTFOLIOS) && (
            <ErrorBoundary
              message="Fund At a Glance information is currently unavailable due to a technical issue. Please try again later."
              title={
                fund.fundGroup === MODEL_PORTFOLIOS
                  ? 'Models At-a-Glance'
                  : 'Fund At-a-Glance'
              }
            >
              <OverviewFundAtAGlance
                fundSummary={fund.summary}
                portfolioHighlightDetail={fund.portfolioHighlightDetail}
                sevenDayYields={sevenDayYieldsData}
                fundGroup={fund.fundGroup}
              />
            </ErrorBoundary>
          )}

        {fund.indexAtaGlance && isPresented(fund, 'indexAtaGlance') && (
          <OverviewIndexAtAGlance indexAtaGlance={fund.indexAtaGlance} />
        )}
      </div>
    </section>
  )
}

Overview.propTypes = {
  fund: PropTypes.object.isRequired,
  investorFundDetail: PropTypes.object,
}

export default Overview
